import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

export default function PickupPage() {
  const { currentBranch, user } = useAuth();
  const [records, setRecords] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [filters, setFilters] = useState({ 
    branch_id: currentBranch?.branch_id || '', 
    date: new Date().toISOString().split('T')[0] 
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    async function loadBranches() {
      try {
        const response = await api.get('/admin/branches');
        setBranches(response.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load branches');
      }
    }
    loadBranches();
  }, []);

  useEffect(() => {
    if (currentBranch) {
      setFilters(p => ({ ...p, branch_id: currentBranch.branch_id }));
    }
  }, [currentBranch]);

  useEffect(() => {
    loadPickup();
  }, [filters.branch_id, filters.date]);

  async function loadPickup() {
    try {
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.date) params.date = filters.date;
      const response = await api.get('/admin/pickup', { params });
      setRecords(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load pickup records');
    }
  }

  const updateStatus = async (recordId, newStatus) => {
    setError('');
    setSuccessMessage('');
    try {
      await api.put(`/admin/pickup/${recordId}`, { status: newStatus });
      setSuccessMessage(`Pickup status updated to "${newStatus}" successfully`);
      loadPickup();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update pickup status');
    }
  };

  const sortedRecords = sortItems(records, sortOption, 'student_name', 'created_at');

  return (
    <div>
      <PageHeader title="Pickup Management" subtitle="Monitor check-in, checkout, confirmation, and pickup history" />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="filter-row" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        {user?.role !== 'center_manager' ? (
          <select value={filters.branch_id} onChange={(e) => setFilters((p) => ({ ...p, branch_id: e.target.value }))}>
            <option value="">All branches</option>
            {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
          </select>
        ) : (
          <div style={{
            padding: '8px 16px',
            background: '#f3f4f6',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151'
          }}>
            📍 Branch: {currentBranch?.name}
          </div>
        )}
        <input type="date" value={filters.date} onChange={(e) => setFilters((p) => ({ ...p, date: e.target.value }))} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, cursor: 'pointer', background: '#ffffff' }} />

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Branch</th>
              <th>Date</th>
              <th>Check-in</th>
              <th>Checkout</th>
              <th>Status / Update</th>
              <th>Picked by</th>
            </tr>
          </thead>
          <tbody>
            {sortedRecords.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  No pickup records found for the selected date/branch.
                </td>
              </tr>
            ) : (
              sortedRecords.map((record) => (
                <tr key={record.id}>
                  <td style={{ fontWeight: 600, color: '#1f2937' }}>{record.student_name || '-'}</td>
                  <td>{record.branch_name || '-'}</td>
                  <td>{record.pickup_date ? String(record.pickup_date).split('T')[0] : '-'}</td>
                  <td>{record.checkin_time || '-'}</td>
                  <td>{record.checkout_time || '-'}</td>
                  <td>
                    <select
                      value={record.status || 'waiting'}
                      onChange={(e) => updateStatus(record.id, e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: record.status === 'picked' ? '#d1fae5' : record.status === 'waiting' || record.status === 'checked_in' ? '#fef3c7' : '#f3f4f6',
                        color: record.status === 'picked' ? '#065f46' : record.status === 'waiting' || record.status === 'checked_in' ? '#92400e' : '#374151'
                      }}
                    >
                      <option value="waiting">waiting</option>
                      <option value="picked">picked</option>
                      <option value="checked_in">checked_in</option>
                      <option value="not_arrived">not_arrived</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </td>
                  <td>{record.picked_by || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
